import { useNavigate } from "react-router-dom";
import { useMemo, useRef, useState } from "react";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import useListProjectVariables from "../../../ProjectVariables/api/useListProjectVariables";
import { AdminFormPage, saveErrorToast } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { GovernanceForm } from "../GovernanceForm/GovernanceForm";
import { useGovernanceMutations } from "../api/useGovernanceMutations";
import type { GovernanceFormValues } from "../types/Governance";

const FORM_ID = "governance-form";

const initial: GovernanceFormValues = {
  name: "",
  resourceId: null,
  required: false,
  calculationType: "fixed_hours",
  fixedHours: null,
  rate: null,
  minimumHours: 0,
  rateRules: [],
  projectVariables: [],
  filterType: "all_services",
  filterId: null,
  allocationMethod: null,
  phaseId: null,
  sectionId: null,
  assignEffortToService: false,
};

export const New = () => {
  const navigate = useNavigate();
  const { create } = useGovernanceMutations();
  const { allProjectVariables, isLoading: variablesLoading } =
    useListProjectVariables("governance");
  // ProjectVariablesV2 reads its variables once, on mount, so the form waits
  // for the definitions. Keyed on loading: the hook builds a new array every
  // render.
  const initialValues = useMemo<GovernanceFormValues>(
    () => ({ ...initial, projectVariables: allProjectVariables }),
    [variablesLoading]
  );
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  const nextActionRef = useRef<"back" | "add-another">("back");

  const handleSubmit = async (values: GovernanceFormValues) => {
    const nextAction = nextActionRef.current;
    nextActionRef.current = "back";
    setSubmitting(true);
    try {
      const result: any = await create(values);
      if (nextAction === "add-another") {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: "Governance created." });
      } else {
        const newId = result?.data?.id;
        if (newId) {
          navigate(`../${newId}/edit`, {
            state: {
              toast: { variant: "success", text: "Governance created." },
            },
          });
        } else {
          setToast({ variant: "success", text: "Governance created." });
        }
      }
    } catch (err) {
      console.error("Failed to create governance", err);
      setToast(saveErrorToast(err, "governance"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndAddAnother = () => {
    nextActionRef.current = "add-another";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  if (variablesLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminFormPage
        title="Add a Governance"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={handleSaveAndAddAnother}
        isSubmitting={submitting}
      >
        <GovernanceForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
